import { useDocumentTabDataContextOptional } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useLiveTargetValues } from 'features/knowledgeDocumentViewer/hooks/useLiveTargetValues'
import { useMemo } from 'react'

export interface LiveProcessValue {
  datapointId: string
  piTag: string
  value: number
  unit?: string
  measuredAt: Date
  isStale: boolean
}

export function useLiveProcessDataValues(
  labels: readonly (string | undefined)[]
): Map<string, LiveProcessValue> {
  const getMatchedDatapoints =
    useDocumentTabDataContextOptional()?.getMatchedDatapoints
  const targets = useMemo(
    () =>
      [...new Set(labels)].flatMap((label) =>
        label
          ? [
              {
                key: label,
                label,
                datapoints: getMatchedDatapoints?.(label) ?? [],
              },
            ]
          : []
      ),
    [labels, getMatchedDatapoints]
  )
  return useLiveTargetValues(targets)
}

export type LiveValueDetailTier = 0 | 1 | 2 | 3

const TIER_CHAR_BUDGET: Record<LiveValueDetailTier, number> = {
  0: 3,
  1: 4,
  2: 5,
  3: 5,
}

const budgetedLength = (formatted: string): number =>
  formatted.replace(/,/g, '').length

export function formatLiveProcessValue(
  live: LiveProcessValue,
  tier: LiveValueDetailTier = 3
): string {
  const value = formatValueToBudget(live.value, tier)
  if (tier !== 3) return value

  const unit = normalizeUnit(live.unit)
  return unit ? `${value} ${unit}` : value
}

function formatValueToBudget(value: number, tier: LiveValueDetailTier): string {
  const magnitude = Math.abs(value)
  if (magnitude < 0.01 && value !== 0) return value.toExponential(1)

  const candidates =
    magnitude >= 1000000
      ? [`${(value / 1000000).toFixed(1)}M`, `${Math.round(value / 1000000)}M`]
      : magnitude >= 100000 || (magnitude >= 1000 && tier <= 1)
        ? [`${(value / 1000).toFixed(1)}K`, `${Math.round(value / 1000)}K`]
        : magnitude >= 1000
          ? [Math.round(value).toLocaleString('en-US')]
          : [value.toFixed(2), value.toFixed(1), value.toFixed(0)]

  const budget = TIER_CHAR_BUDGET[tier]
  return (
    candidates.find((candidate) => budgetedLength(candidate) <= budget) ??
    candidates[candidates.length - 1]
  )
}

const NO_UNIT_SENTINELS = new Set(['NO UNIT', 'NONE', 'N/A', 'UNITLESS'])
const MAX_UNIT_LENGTH = 6

function normalizeUnit(unit: string | undefined): string | undefined {
  const trimmed = unit?.trim().toUpperCase()
  if (!trimmed || NO_UNIT_SENTINELS.has(trimmed)) return undefined
  return trimmed.length <= MAX_UNIT_LENGTH ? trimmed : undefined
}
